/**
 * MarketplaceAdminQueue - Admin review queue for pending marketplace listings
 * Split into Open Reviews (pending) and Past Reviews (approved/rejected).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import { getCategoryById } from './MarketplaceCategories';

const MarketplaceAdminQueue = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const {
    pendingReviewListings,
    reviewedListings,
    loading,
    categories,
    initializeMarketplace,
    approveListing,
    rejectListing,
    loadPendingReviewListings,
    loadReviewedListings,
  } = useMarketplace();

  useEffect(() => {
    initializeMarketplace();
    loadReviewedListings();
  }, [initializeMarketplace, loadReviewedListings]);

  const [activeTab, setActiveTab] = useState('open');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(null);

  if (!isAdmin) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Admin Access Required"
          description="Only administrators can access the review queue."
          action={
            <Button variant="primary" onClick={() => navigate('/marketplace')}>
              Back to Marketplace
            </Button>
          }
        />
      </div>
    );
  }

  const handleApprove = async (listing) => {
    setProcessing(listing.id);
    try {
      await approveListing(listing.id);
      toast.success(`"${listing.title}" has been approved.`);
      // Refresh both lists so it moves from Open → Past
      await Promise.all([loadPendingReviewListings(), loadReviewedListings()]);
    } catch (err) {
      console.error('Error approving listing:', err);
      toast.error('Failed to approve listing. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = (listing) => {
    setSelectedListing(listing);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!selectedListing || !rejectionReason.trim()) return;

    setProcessing(selectedListing.id);
    try {
      await rejectListing(selectedListing.id, rejectionReason);
      toast.success(`"${selectedListing.title}" has been rejected.`);
      setShowRejectModal(false);
      setSelectedListing(null);
      setRejectionReason('');
      // Refresh both lists so it moves from Open → Past
      await Promise.all([loadPendingReviewListings(), loadReviewedListings()]);
    } catch (err) {
      console.error('Error rejecting listing:', err);
      toast.error('Failed to reject listing. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const tabs = [
    {
      id: 'open',
      label: 'Open Reviews',
      count: pendingReviewListings.length,
      countColor: pendingReviewListings.length > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500',
    },
    {
      id: 'past',
      label: 'Past Reviews',
      count: reviewedListings.length,
      countColor: 'bg-gray-100 text-gray-500',
    },
  ];

  return (
    <div className="w-full">
      {/* Sub-header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600 text-sm">
          Review and manage marketplace listing submissions
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            await Promise.all([loadPendingReviewListings(), loadReviewedListings()]);
          }}
        >
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${tab.countColor}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Open Reviews Tab */}
      {activeTab === 'open' && (
        <>
          {loading && pendingReviewListings.length === 0 ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="large" message="Loading queue..." />
            </div>
          ) : pendingReviewListings.length === 0 ? (
            <EmptyState
              icon="✅"
              title="All caught up!"
              description="There are no listings pending review."
            />
          ) : (
            <div className="space-y-4">
              {pendingReviewListings.map(listing => {
                const category = getCategoryById(listing.category, categories);
                const isProcessing = processing === listing.id;

                return (
                  <Card key={listing.id} padding="md">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{category.icon}</span>
                          <span className="text-sm text-gray-500">{category.name}</span>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{listing.title}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{listing.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span>By: {listing.sellerDisplayName}</span>
                          <span>{listing.isFree ? 'Free' : `$${listing.price}`}</span>
                          <span>{listing.nodeCount} nodes</span>
                          <span className="text-gray-400">
                            Submitted {listing.createdAt?.toDate?.()?.toLocaleDateString() || '—'}
                          </span>
                        </div>
                      </div>

                      {/* Flags */}
                      <div className="lg:w-56 flex-shrink-0">
                        {(listing.failedChecks?.length > 0 || listing.reportCount > 0) && (
                          <p className="text-xs font-medium text-gray-400 uppercase mb-2">Flags</p>
                        )}
                        {listing.failedChecks?.map((check, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-sm mb-1">
                            <span>⚠️</span>
                            <span className="text-gray-700">{formatCheckName(check)}</span>
                          </div>
                        ))}
                        {listing.reportCount > 0 && (
                          <div className="flex items-center gap-1.5 text-sm mb-1">
                            <span>🚩</span>
                            <span className="text-gray-700">
                              {listing.reportCount} report{listing.reportCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex lg:flex-col gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                        >
                          Review
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleApprove(listing)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? '...' : '✓ Approve'}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleReject(listing)}
                          disabled={isProcessing}
                        >
                          ✕ Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Past Reviews Tab */}
      {activeTab === 'past' && (
        <>
          {reviewedListings.length === 0 ? (
            <EmptyState
              icon="📋"
              title="No review history yet"
              description="Approved and rejected listings will appear here."
            />
          ) : (
            <div className="space-y-3">
              {reviewedListings.map(listing => {
                const category = getCategoryById(listing.category, categories);
                const isApproved = listing.status === 'active';
                const isDeleted = listing.status === 'deleted';

                const statusBadge = isDeleted
                  ? { cls: 'bg-gray-200 text-gray-600', label: '🗑 Deleted' }
                  : isApproved
                    ? { cls: 'bg-green-100 text-green-700', label: '✓ Approved' }
                    : { cls: 'bg-red-100 text-red-700', label: '✕ Rejected' };

                // Most-recent admin action time and actor
                const actionTimestamp = isDeleted
                  ? listing.deletedAt
                  : listing.reviewedAt;
                const actionBy = isDeleted
                  ? listing.deletedBy
                  : listing.reviewedBy;

                return (
                  <Card key={listing.id} padding="md" className={isDeleted ? 'opacity-70' : ''}>
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xl">{category.icon}</span>
                          <span className="text-sm text-gray-500">{category.name}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${statusBadge.cls}`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        <h3 className={`font-semibold mb-1 ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {listing.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          <span>By: {listing.sellerDisplayName}</span>
                          <span>{listing.isFree ? 'Free' : `$${listing.price}`}</span>
                          {actionBy && <span>{isDeleted ? 'Deleted' : 'Reviewed'} by: {actionBy}</span>}
                          {actionTimestamp && (
                            <span>{actionTimestamp?.toDate?.()?.toLocaleDateString() || '—'}</span>
                          )}
                        </div>
                        {listing.status === 'rejected' && listing.rejectionReason && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-700">
                            <span className="font-medium">Rejection reason: </span>
                            {listing.rejectionReason}
                          </div>
                        )}
                      </div>

                      {!isDeleted && (
                        <div className="flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedListing && (
        <Modal
          isOpen
          onClose={() => {
            setShowRejectModal(false);
            setSelectedListing(null);
          }}
          title="Reject Listing"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                Rejecting: <strong>{selectedListing.title}</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this listing is being rejected. This will be shown to the seller."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedListing(null);
                }}
                disabled={processing === selectedListing?.id}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmReject}
                disabled={!rejectionReason.trim() || processing === selectedListing?.id}
              >
                {processing === selectedListing?.id ? 'Rejecting...' : 'Reject Listing'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const formatCheckName = (check) => {
  const names = {
    description_length: 'Description too short',
    node_count: 'Not enough script nodes',
    profanity: 'Contains inappropriate content',
    price: 'Invalid price',
    stripe_connect: 'Stripe account not verified',
    community_reports: 'Multiple user reports',
  };
  return names[check] || check;
};

export default MarketplaceAdminQueue;
